/**
 * P4 — pure helpers for the easy-KPI screen. No React / no I/O so they are
 * directly unit-testable (see kpi-helpers.test.ts).
 */
import type {
  KpiTarget,
  TargetPeriodType,
  TargetPlatform,
  TargetType,
  UpsertKpiTargetBody,
} from '../../lib/api/client-reports/kpi-targets-api';

/** A single editable target row in the KPI screen's working draft. */
export interface KpiDraftRow {
  /** Stable local key (survives re-render; not sent to the server). */
  rowKey: string;
  /** The server target id, or null for a freshly added row. */
  serverId: string | null;
  metric_key: string;
  platform: TargetPlatform;
  period_type: TargetPeriodType;
  period_start: string; // YYYY-MM-DD
  /** Kept as a string so an empty input = "no target" (not 0). */
  value: string;
  target_type: TargetType;
  currency: string;
  notes: string;
}

// One-click metric packs — prefill rows the user just types values into.
export interface MetricPack {
  key: string;
  label: string;
  metrics: Array<{ metric_key: string; target_type?: TargetType }>;
}

export const METRIC_PACKS: MetricPack[] = [
  {
    key: 'ecommerce',
    label: 'E-commerce',
    metrics: [
      { metric_key: 'roas' },
      { metric_key: 'cpa', target_type: 'cap' },
      { metric_key: 'aov' },
      { metric_key: 'orders' },
    ],
  },
  {
    key: 'leadgen',
    label: 'Lead-gen',
    metrics: [
      // `cpl` is a synonym of the `cpa` global, not a metric key of its own —
      // sending it fails the backend's metric_key validation.
      { metric_key: 'cpa', target_type: 'cap' },
      { metric_key: 'leads' },
      { metric_key: 'conversion_rate' },
    ],
  },
  {
    key: 'awareness',
    label: 'Awareness',
    metrics: [
      { metric_key: 'cpm', target_type: 'cap' },
      { metric_key: 'ctr' },
    ],
  },
];

/**
 * Derive a backend-legal metric key from a human label ("Contribution margin"
 * → "contribution_margin"). The backend enforces /^[a-z0-9_]+$/ with max 64, so
 * anything else collapses to an underscore and the edges are trimmed.
 */
export function metricKeyFromLabel(label: string): string {
  return (label ?? '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .slice(0, 64)
    .replace(/^_+|_+$/g, '');
}

/** Mirrors the backend's metric-key rule (MetricDefinitionController::store). */
export function isValidMetricKey(key: string): boolean {
  return /^[a-z0-9_]+$/.test(key) && key.length <= 64;
}

/** Direction → sensible default target_type (lower-is-better ⇒ cap). */
export function defaultTargetTypeForDirection(
  direction: string | undefined | null
): TargetType {
  return direction === 'lower_better' ? 'cap' : 'target';
}

let rowKeySeq = 0;
export function nextRowKey(prefix = 'row'): string {
  rowKeySeq += 1;
  return `${prefix}-${Date.now()}-${rowKeySeq}`;
}

/** Identity tuple for a target (matches the backend's unique constraint). */
export function tupleKey(r: {
  metric_key: string;
  platform: string;
  period_type: string;
  period_start: string;
}): string {
  return `${r.metric_key}|${r.platform}|${r.period_type}|${r.period_start}`;
}

/**
 * Append a metric pack's rows to the current table rows. Metrics already
 * present (same metric_key at year scope) are skipped so re-clicking a pack
 * never duplicates. `year` sets the annual period_start. Direction defaults the
 * target_type unless the pack pins one. Returns a NEW array (pure).
 */
export function applyMetricPack(
  rows: KpiDraftRow[],
  pack: MetricPack,
  opts: {
    year: number;
    currency: string;
    directionOf: (metricKey: string) => string | undefined;
    platform?: TargetPlatform;
  }
): KpiDraftRow[] {
  const platform = opts.platform ?? 'blended';
  const period_start = `${opts.year}-01-01`;
  const existing = new Set(
    rows.map((r) => `${r.metric_key}|${r.period_type}|${r.period_start}`)
  );
  const additions: KpiDraftRow[] = [];
  for (const m of pack.metrics) {
    const dedupe = `${m.metric_key}|year|${period_start}`;
    if (existing.has(dedupe)) continue;
    existing.add(dedupe);
    additions.push({
      rowKey: nextRowKey('pack'),
      serverId: null,
      metric_key: m.metric_key,
      platform,
      period_type: 'year',
      period_start,
      value: '',
      target_type:
        m.target_type ?? defaultTargetTypeForDirection(opts.directionOf(m.metric_key)),
      currency: opts.currency,
      notes: '',
    });
  }
  return [...rows, ...additions];
}

// A selectable period within a year (drives the table's period dropdown).
export interface PeriodOption {
  value: string; // `${period_type}:${period_start}`
  label: string;
  period_type: TargetPeriodType;
  period_start: string;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function periodValue(period_type: string, period_start: string): string {
  return `${period_type}:${period_start}`;
}

/** All in-year period choices: Full year, Q1–Q4, Jan–Dec. */
export function buildPeriodOptions(year: number): PeriodOption[] {
  const opts: PeriodOption[] = [
    {
      value: periodValue('year', `${year}-01-01`),
      label: 'Full year',
      period_type: 'year',
      period_start: `${year}-01-01`,
    },
  ];
  for (let q = 0; q < 4; q += 1) {
    const startMonth = q * 3 + 1;
    const period_start = `${year}-${pad2(startMonth)}-01`;
    opts.push({
      value: periodValue('quarter', period_start),
      label: `Q${q + 1}`,
      period_type: 'quarter',
      period_start,
    });
  }
  for (let m = 1; m <= 12; m += 1) {
    const period_start = `${year}-${pad2(m)}-01`;
    opts.push({
      value: periodValue('month', period_start),
      label: MONTHS[m - 1],
      period_type: 'month',
      period_start,
    });
  }
  return opts;
}

export function monthLabel(monthIndex0: number): string {
  return MONTHS[monthIndex0] ?? '';
}

export function monthPeriodStart(year: number, monthIndex0: number): string {
  return `${year}-${pad2(monthIndex0 + 1)}-01`;
}

/** Parse a user-typed value; empty/invalid ⇒ null ("no target set"). */
export function parseValue(v: string): number | null {
  const trimmed = (v ?? '').toString().trim();
  if (trimmed === '') return null;
  const n = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function bodyOf(r: KpiDraftRow, value: number): UpsertKpiTargetBody {
  return {
    metric_key: r.metric_key,
    platform: r.platform,
    period_type: r.period_type,
    period_start: r.period_start,
    target_value: value,
    target_type: r.target_type,
    currency: r.currency || null,
    notes: r.notes || null,
  };
}

export interface TargetMutations {
  upserts: UpsertKpiTargetBody[];
  deletes: string[];
}

/**
 * Diff the working draft against the originally-loaded targets and compute the
 * minimal set of upserts (POST) + deletes (DELETE id).
 *
 * Rules:
 *   - A row with a blank/invalid value is "not set": if it had a server row,
 *     that row is deleted; otherwise it is ignored.
 *   - A NEW row (no serverId) with a value ⇒ upsert.
 *   - An existing row whose IDENTITY tuple changed ⇒ delete the old id + upsert
 *     the new tuple (POST upserts on the tuple).
 *   - An existing row (same tuple) whose value/type/currency/notes changed ⇒
 *     upsert. Unchanged ⇒ skipped.
 *   - Any original target no longer represented by any draft row ⇒ delete.
 */
export function computeTargetMutations(
  original: KpiTarget[],
  draft: KpiDraftRow[]
): TargetMutations {
  const upserts: UpsertKpiTargetBody[] = [];
  const deletes: string[] = [];
  const originalById = new Map(original.map((t) => [t.id, t]));
  const keptIds = new Set<string>();

  for (const r of draft) {
    const value = parseValue(r.value);
    const orig = r.serverId ? originalById.get(r.serverId) : undefined;

    if (value === null) {
      // Cleared — delete the backing server row if there was one.
      if (r.serverId) deletes.push(r.serverId);
      continue;
    }

    if (!orig) {
      // Brand-new row (or a row whose serverId no longer exists).
      upserts.push(bodyOf(r, value));
      continue;
    }

    keptIds.add(orig.id);
    const tupleChanged = tupleKey(orig) !== tupleKey(r);
    const fieldsChanged =
      orig.target_value !== value ||
      orig.target_type !== r.target_type ||
      (orig.currency || '') !== (r.currency || '') ||
      (orig.notes || '') !== (r.notes || '');

    if (tupleChanged) {
      // The unique tuple moved — the old row would be orphaned; remove it and
      // upsert the new tuple.
      deletes.push(orig.id);
      keptIds.delete(orig.id);
      upserts.push(bodyOf(r, value));
    } else if (fieldsChanged) {
      upserts.push(bodyOf(r, value));
    }
  }

  // Any original row not represented (and not already queued for delete) ⇒ gone.
  for (const t of original) {
    if (!keptIds.has(t.id) && !deletes.includes(t.id)) {
      const stillPresent = draft.some(
        (r) => r.serverId === t.id && parseValue(r.value) !== null
      );
      if (!stillPresent) deletes.push(t.id);
    }
  }

  return { upserts, deletes };
}
